import axios from "axios";
import { useAuthStore } from "@/stores/auth";
import router from "@/router";
import Swal from "sweetalert2";
import SecureLS from "secure-ls";

const ls = new SecureLS();
let isHandlingTokenExpired = false;

const isTokenExpiredPayload = (payload) =>
  payload?.message === "TOKEN_EXPIRED";

const handleTokenExpired = async () => {
  if (isHandlingTokenExpired) return;
  isHandlingTokenExpired = true;

  try {
    const authStore = useAuthStore();
    authStore.logout();
  } catch (storeError) {
    ls.remove("token");
    ls.remove("user");
    ls.remove("permissions");
  }

  await Swal.fire({
    icon: "warning",
    title: "Session Expired",
    text: "Your session has expired. Please log in again.",
    confirmButtonText: "OK",
    allowOutsideClick: false,
  });

  const currentRoute = router.currentRoute.value.fullPath;
  router.push({
    path: "/login",
    query: {
      redirectFrom: currentRoute !== "/login" ? currentRoute : undefined,
    },
  });
};

// Create axios instance
const apiClient = axios.create({
  baseURL: window.getRuntimeConfig("VUE_APP_API_URL", process.env.VUE_APP_API_URL),
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

const requestHandler = (config) => {
  const token = ls.get("token");

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (process.env.NODE_ENV === "development") {
    console.log(
      `API Request: ${config.method?.toUpperCase()} ${config.url}`,
      config
    );
  }

  return config;
};

const requestErrorHandler = (error) => {
  console.error("Request interceptor error:", error);
  return Promise.reject(error);
};

const responseHandler = async (response) => {
  if (process.env.NODE_ENV === "development") {
    console.log(
      `API Response: ${response.config.method?.toUpperCase()} ${
        response.config.url
      }`,
      response
    );
  }

  if (response?.data?.success === false && isTokenExpiredPayload(response.data)) {
    console.warn("TOKEN_EXPIRED - Logging out user");
    await handleTokenExpired();
    const tokenError = new Error("TOKEN_EXPIRED");
    tokenError.isTokenExpired = true;
    return Promise.reject(tokenError);
  }

  return response;
};

const responseErrorHandler = async (error) => {
  if (error?.isTokenExpired || error?.message === "TOKEN_EXPIRED") {
    return Promise.reject(error);
  }

  if (isTokenExpiredPayload(error?.response?.data)) {
    console.warn("TOKEN_EXPIRED - Logging out user");
    await handleTokenExpired();
    return Promise.reject(error);
  }

  const originalRequest = error.config;

  // Callers that handle failures themselves (e.g. optional startup calls).
  if (originalRequest?.skipGlobalErrorHandler && error.response?.status !== 401) {
    return Promise.reject(error);
  }

    // Handle different error status codes
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401: {
          // Unauthorized - token expired or invalid
          console.warn("401 Unauthorized - Logging out user");

          await handleTokenExpired();
          break;
        }

        case 403: {
          // Forbidden - insufficient permissions
          console.error("403 Forbidden - Insufficient permissions");
          await Swal.fire({
            icon: "error",
            title: "Access Denied",
            text: "You do not have permission to perform this action.",
            confirmButtonText: "OK",
          });
          break;
        }

        case 404: {
          // Not Found
          console.error("404 Not Found:", originalRequest.url);
          await Swal.fire({
            icon: "error",
            title: "Not Found",
            text: "The requested resource was not found.",
            confirmButtonText: "OK",
          });
          break;
        }

        case 422: {
          // Validation Error - let components handle this
          console.warn("422 Validation Error:", data);
          break;
        }

        case 429: {
          // Too Many Requests
          console.error("429 Too Many Requests");
          await Swal.fire({
            icon: "warning",
            title: "Too Many Requests",
            text: "Please slow down and try again in a few moments.",
            confirmButtonText: "OK",
          });
          break;
        }

        case 500:
        case 502:
        case 503:
        case 504: {
          // Server Errors
          console.error(`${status} Server Error:`, error);
          await Swal.fire({
            icon: "error",
            title: "Server Error",
            text: "Something went wrong on our end. Please try again later.",
            confirmButtonText: "OK",
          });
          break;
        }

        default: {
          // Other errors
          console.error(`API Error ${status}:`, error);
          await Swal.fire({
            icon: "error",
            title: "Error",
            text:
              data?.message ||
              "An unexpected error occurred. Please try again.",
            confirmButtonText: "OK",
          });
          break;
        }
      }
    } else if (error.request) {
      // Network error
      console.error("Network Error:", error);
      await Swal.fire({
        icon: "error",
        title: "Network Error",
        text: "Unable to connect to the server. Please check your internet connection.",
        confirmButtonText: "OK",
      });
    } else {
      // Something else happened
      console.error("Unexpected Error:", error);
      await Swal.fire({
        icon: "error",
        title: "Unexpected Error",
        text: "An unexpected error occurred. Please try again.",
        confirmButtonText: "OK",
      });
    }

    return Promise.reject(error);
};

// Request interceptor to add auth token
apiClient.interceptors.request.use(requestHandler, requestErrorHandler);
axios.interceptors.request.use(requestHandler, requestErrorHandler);

// Response interceptor for global error handling
apiClient.interceptors.response.use(responseHandler, responseErrorHandler);
axios.interceptors.response.use(responseHandler, responseErrorHandler);

// Set axios as default for the app
axios.defaults = apiClient.defaults;

export default apiClient;
